import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import isNil from 'lodash/isNil';
import { useQuery } from '@apollo/client';
import { freeze } from '@reduxjs/toolkit';
import {
  DashboardTemplateScope,
  IDashboardTemplateFieldsFragment,
  IGetDashboardTemplatesByScopeQuery,
  IGetDashboardTemplatesByScopeQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { GET_DASHBOARD_TEMPLATES_BY_SCOPE } from '../../../utils/apollo/dashboards';
import DashboardWelcomeScreen from './DashboardWelcomeScreen';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import DashboardInstanceLoader from '../components/DashboardInstanceLoader';
import { assertNotNil } from '../../../utils/types';
import { appRoutes } from '../../../path';

/**
 * We render this component for /dash URL
 *
 * At first, we check lastVisitedDashboardPathname. If exists - we will
 * navigate user to last visited dashboard.
 *
 * Next we will load all user templates and will try to get 1st in the list.
 * If it exists - navigate user to it. Otherwise - show a Welcome screen.
 */

const DASHBOARD_TEMPLATE_QUERY_VARIABLES =
  freeze<IGetDashboardTemplatesByScopeQueryVariables>(
    {
      term: '',
      scopes: [
        DashboardTemplateScope.Favorite,
        DashboardTemplateScope.CreatedByMe,
        DashboardTemplateScope.Published,
        DashboardTemplateScope.SharedWithMe,
      ],
    },
    true,
  );

const DashboardIndexPage = () => {
  const lastVisitedDashboardPathname = useDashboardSelector(
    'lastVisitedDashboardPathname',
  );

  const { data, loading } = useQuery<
    IGetDashboardTemplatesByScopeQuery,
    IGetDashboardTemplatesByScopeQueryVariables
  >(GET_DASHBOARD_TEMPLATES_BY_SCOPE, {
    variables: DASHBOARD_TEMPLATE_QUERY_VARIABLES,
    skip: !isNil(lastVisitedDashboardPathname),
  });

  const firstTemplate = useMemo<IDashboardTemplateFieldsFragment | null>(() => {
    if (!data) {
      return null;
    }

    return (
      data.dashboardTemplatesByScope.find(
        (templateGroup) =>
          templateGroup.entities.length > 0 && templateGroup.entities[0],
      )?.entities?.[0] ?? null
    );
  }, [data]);

  if (!isNil(lastVisitedDashboardPathname)) {
    return <Navigate replace={true} to={lastVisitedDashboardPathname} />;
  }

  if (loading) {
    return <DashboardInstanceLoader />;
  }

  // When user doesn't have any dashboards - show welcome screen
  if (!firstTemplate) {
    return <DashboardWelcomeScreen />;
  }

  // If exists, navigate to first template
  assertNotNil(firstTemplate);

  return (
    <Navigate
      replace={true}
      to={appRoutes.dashboard.generateLink(firstTemplate.baseId)}
    />
  );
};

export default DashboardIndexPage;
